import React, { useMemo } from "react";
import { formatPrice } from "./marketFormatters";
import "./market-24h-pricerange.css";

function toNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? parseFloat(String(v).replace(/,/g, "")) : Number(v);
  if (Number.isNaN(n) || !Number.isFinite(n)) return null;
  return n;
}

/**
 * Dashed 24h range, dot at last price, labels + hover: left=low, center=last, right=high.
 */
const Market24hPriceRange = React.memo(function Market24hPriceRange({ low, high, lastPrice, tickSize }) {
  const { leftPct, lowStr, highStr, lastStr, empty } = useMemo(() => {
    const lo = toNum(low);
    const hi = toNum(high);
    const last = toNum(lastPrice);
    const tick = tickSize;

    if (lo == null || hi == null) {
      return {
        leftPct: 50,
        lowStr: "—",
        highStr: "—",
        lastStr: last != null ? formatPrice(last, tick) : "—",
        empty: true,
      };
    }

    const minP = Math.min(lo, hi);
    const maxP = Math.max(lo, hi);
    const span = maxP - minP;
    const lastClamped = last != null ? last : (minP + maxP) / 2;
    let pct = 50;
    if (span > 0) {
      pct = ((lastClamped - minP) / span) * 100;
      pct = Math.max(0, Math.min(100, pct));
    }

    return {
      leftPct: pct,
      lowStr: formatPrice(minP, tick),
      highStr: formatPrice(maxP, tick),
      lastStr: last != null ? formatPrice(last, tick) : formatPrice((minP + maxP) / 2, tick),
      empty: false,
    };
  }, [low, high, lastPrice, tickSize]);

  return (
    <div className={`m24h-range${empty ? " m24h-range--empty" : ""}`} onClick={(e) => e.stopPropagation()}>
      <div className="m24h-range__line-wrap" aria-label="24 hour price range">
        <div className="m24h-range__dashed" />
        <div
          className="m24h-range__dot"
          style={{ left: `${leftPct}%` }}
          title={!empty ? `Last price ${lastStr}` : undefined}
        />
        <div className="m24h-range__zones">
          <div className="m24h-range__zone" title={`24h Low: $${lowStr}`}>
            <div className="m24h-range__pop">
              <span>24h Low</span>
              <b>${lowStr}</b>
            </div>
          </div>
          <div className="m24h-range__zone" title={`Last Price: $${lastStr}`}>
            <div className="m24h-range__pop">
              <span>Last Price</span>
              <b>${lastStr}</b>
            </div>
          </div>
          <div className="m24h-range__zone" title={`24h High: $${highStr}`}>
            <div className="m24h-range__pop">
              <span>24h High</span>
              <b>${highStr}</b>
            </div>
          </div>
        </div>
      </div>
      <div className="m24h-range__labels">
        <div className="m24h-range__label m24h-range__label--low">
          <span className="m24h-range__subl">24h Low</span>
          <span className="m24h-range__pr">${lowStr}</span>
        </div>
        <div className="m24h-range__label m24h-range__label--mid">
          <span className="m24h-range__subl">Last Price</span>
          <span className="m24h-range__pr">${lastStr}</span>
        </div>
        <div className="m24h-range__label m24h-range__label--high">
          <span className="m24h-range__subl">24h High</span>
          <span className="m24h-range__pr">${highStr}</span>
        </div>
      </div>
    </div>
  );
});

Market24hPriceRange.displayName = "Market24hPriceRange";
export default Market24hPriceRange;
